<template>
    <div class="manifest-pipeline-editor">
        <div class="manifest-pipeline-editor__header mb-1">
            <div class="d-flex align-items-center justify-content-between">
                <h5 class="mb-0">{{ title }}</h5>
                <b-button
                    size="sm"
                    variant="outline-primary"
                    class="manifest-pipeline-editor__add-button"
                    :disabled="disabled
                        || (requiresTargetField && !targetField)
                        || (!availableComponents.length && !(allowConditions && availableConditions.length))"
                    @click="showPicker = !showPicker"
                >
                    <feather-icon :icon="showPicker ? 'XIcon' : 'PlusIcon'" class="mr-50" />
                    {{ showPicker ? 'Cancel' : 'Add step' }}
                </b-button>
            </div>
            <small class="d-block text-muted mt-25">
                {{ description }}
            </small>
        </div>

        <div v-if="showPicker" class="manifest-pipeline-editor__picker mb-1">
            <b-button-group v-if="allowConditions" size="sm" class="mb-1">
                <b-button
                    :variant="pickerMode === 'step' ? 'primary' : 'outline-secondary'"
                    @click="pickerMode = 'step'"
                >
                    Component step
                </b-button>
                <b-button
                    :variant="pickerMode === 'conditional' ? 'primary' : 'outline-secondary'"
                    @click="pickerMode = 'conditional'"
                >
                    Conditional branch
                </b-button>
            </b-button-group>

            <b-form-group
                v-if="pickerMode === 'step'"
                :label="pickerLabel"
                label-for="manifest-pipeline-component"
                class="mb-0"
            >
                <v-select
                    id="manifest-pipeline-component"
                    v-model="selectedComponent"
                    :options="availableComponents"
                    :get-option-key="componentOptionKey"
                    :clearable="false"
                    :disabled="disabled"
                    label="title"
                    :placeholder="pickerPlaceholder"
                    @input="addSelectedComponent"
                >
                    <template #option="component">
                        <div class="d-flex align-items-center justify-content-between">
                            <span>{{ component.title }}</span>
                            <b-badge variant="light-secondary">{{ component.kind }}</b-badge>
                        </div>
                        <div class="d-flex align-items-center justify-content-between mt-25">
                            <small class="text-muted">
                                {{ component.key }}@{{ component.version }}
                            </small>
                            <small class="text-muted">
                                {{ component.inputType || 'mixed' }} → {{ component.outputType || 'mixed' }}
                            </small>
                        </div>
                        <small
                            v-if="component.description"
                            class="manifest-pipeline-editor__option-description text-muted"
                        >
                            {{ component.description }}
                        </small>
                    </template>
                    <template #selected-option="component">
                        {{ component.title }} · v{{ component.version }}
                    </template>
                </v-select>
            </b-form-group>

            <b-form-group
                v-else
                label="Branch condition"
                label-for="manifest-pipeline-condition"
                class="mb-0"
            >
                <v-select
                    id="manifest-pipeline-condition"
                    v-model="selectedCondition"
                    :options="availableConditions"
                    :get-option-key="componentOptionKey"
                    :clearable="false"
                    :disabled="disabled"
                    label="title"
                    placeholder="Choose a condition"
                    @input="addConditionalBranch"
                >
                    <template #option="component">
                        <div>{{ component.title }}</div>
                        <small class="text-muted">
                            {{ component.key }}@{{ component.version }}
                        </small>
                        <small
                            v-if="component.description"
                            class="manifest-pipeline-editor__option-description text-muted"
                        >
                            {{ component.description }}
                        </small>
                    </template>
                </v-select>
            </b-form-group>
        </div>

        <div v-if="steps.length" class="manifest-pipeline-editor__steps">
            <article
                v-for="step in steps"
                :key="step.renderKey"
                class="manifest-pipeline-editor__step"
                :class="{'manifest-pipeline-editor__step--conditional': step.elementType === 'conditional'}"
            >
                <template v-if="step.elementType === 'conditional'">
                    <div class="manifest-pipeline-editor__step-main">
                        <span class="manifest-pipeline-editor__step-number">{{ step.index + 1 }}</span>

                        <div class="manifest-pipeline-editor__step-title">
                            <span class="manifest-pipeline-editor__conditional-label mr-50">If</span>
                            <span class="font-weight-bold mr-50">{{ step.title }}</span>
                            <span
                                v-if="step.description"
                                :id="stepDescriptionId(step.index)"
                                class="manifest-pipeline-editor__info"
                                role="button"
                                tabindex="0"
                                :aria-label="`About ${step.title}`"
                                @click.stop
                            >
                                <feather-icon icon="InfoIcon" size="14" />
                            </span>
                            <b-popover
                                v-if="step.description"
                                :target="stepDescriptionId(step.index)"
                                triggers="hover click blur"
                                placement="bottom"
                            >
                                <template #title>{{ step.title }}</template>
                                {{ step.description }}
                            </b-popover>
                        </div>

                        <span
                            v-if="step.issues.length"
                            class="manifest-pipeline-editor__step-badges"
                        >
                            <b-badge
                                variant="light-warning"
                                :title="elementIssueSummary(step)"
                            >
                                {{ elementIssueLabel(step) }}
                            </b-badge>
                        </span>

                        <div class="manifest-pipeline-editor__step-key">
                            <code>{{ step.when.key }}@{{ step.when.version }}</code>
                        </div>

                        <div class="manifest-pipeline-editor__conditional-flow">
                            <span class="manifest-pipeline-editor__flow-path">
                                <small>Input</small>
                                <code>{{ step.flowInputType }}</code>
                            </span>
                            <span class="manifest-pipeline-editor__flow-path">
                                <small>False · keep</small>
                                <code>{{ step.flowInputType }}</code>
                            </span>
                            <span class="manifest-pipeline-editor__flow-path">
                                <small>True · {{ step.then.length }} steps</small>
                                <code v-if="step.branchOutputType">{{ step.branchOutputType }}</code>
                                <span v-else class="text-warning">Not configured</span>
                            </span>
                            <span
                                class="manifest-pipeline-editor__flow-path manifest-pipeline-editor__flow-path--result"
                                title="Merged type passed to the next pipeline step. Values themselves are not combined."
                            >
                                <small>Result</small>
                                <code>{{ step.flowOutputType }}</code>
                            </span>
                        </div>

                        <div class="manifest-pipeline-editor__step-actions">
                            <b-button
                                size="sm"
                                :variant="isStepExpanded(step.index) ? 'flat-primary' : 'flat-secondary'"
                                class="btn-icon"
                                :title="isStepExpanded(step.index) ? 'Close branch' : 'Configure branch'"
                                :aria-expanded="isStepExpanded(step.index) ? 'true' : 'false'"
                                @click="toggleStep(step.index)"
                            >
                                <feather-icon icon="SlidersIcon" size="16" />
                            </b-button>
                            <b-button
                                size="sm"
                                variant="flat-secondary"
                                class="btn-icon"
                                title="Move branch up"
                                :disabled="disabled || step.index === 0"
                                @click="moveStep(step.index, -1)"
                            >
                                <feather-icon icon="ArrowUpIcon" size="16" />
                            </b-button>
                            <b-button
                                size="sm"
                                variant="flat-secondary"
                                class="btn-icon"
                                title="Move branch down"
                                :disabled="disabled || step.index === steps.length - 1"
                                @click="moveStep(step.index, 1)"
                            >
                                <feather-icon icon="ArrowDownIcon" size="16" />
                            </b-button>
                            <b-button
                                size="sm"
                                variant="flat-danger"
                                class="btn-icon"
                                title="Remove branch"
                                :disabled="disabled"
                                @click="removeStep(step.index)"
                            >
                                <feather-icon icon="Trash2Icon" size="16" />
                            </b-button>
                        </div>
                    </div>

                    <div
                        v-if="isStepExpanded(step.index)"
                        class="manifest-pipeline-editor__arguments manifest-pipeline-editor__branch"
                    >
                        <b-form-group
                            label="When"
                            :label-for="branchConditionId(step.index)"
                        >
                            <v-select
                                :id="branchConditionId(step.index)"
                                :value="step.conditionComponent"
                                :options="availableConditions"
                                :get-option-key="componentOptionKey"
                                :clearable="false"
                                :disabled="disabled"
                                label="title"
                                @input="updateBranchCondition(step.index, $event)"
                            />
                        </b-form-group>

                        <manifest-component-arguments
                            v-if="step.conditionComponent && step.conditionArguments.length"
                            :value="step.when.arguments"
                            :schema="step.conditionComponent.argumentSchema"
                            :disabled="disabled"
                            class="mb-1"
                            @input="updateBranchConditionArguments(step.index, $event)"
                        />

                        <div class="manifest-pipeline-editor__then">
                            <manifest-pipeline-editor
                                :value="step.then"
                                :components="components"
                                :allowed-kinds="branchKinds"
                                title="Then"
                                description="Ordered steps executed when the condition is satisfied."
                                picker-label="Branch component"
                                picker-placeholder="Choose an extractor, normalizer, validator, or transformer"
                                empty-text="Add at least one step to complete this conditional branch."
                                :initial-type="step.flowInputType"
                                :branch-mode="true"
                                :allow-conditions="false"
                                :requires-target-field="false"
                                :show-target-type="false"
                                :disabled="disabled"
                                @input="updateBranchThen(step.index, $event)"
                            />
                        </div>
                    </div>
                </template>

                <template v-else>
                <div class="manifest-pipeline-editor__step-main">
                    <span class="manifest-pipeline-editor__step-number">{{ step.index + 1 }}</span>

                    <div class="manifest-pipeline-editor__step-title">
                        <span class="font-weight-bold mr-50">{{ step.title }}</span>
                        <span
                            v-if="step.description"
                            :id="stepDescriptionId(step.index)"
                            class="manifest-pipeline-editor__info"
                            role="button"
                            tabindex="0"
                            :aria-label="`About ${step.title}`"
                            @click.stop
                        >
                            <feather-icon icon="InfoIcon" size="14" />
                        </span>
                        <b-popover
                            v-if="step.description"
                            :target="stepDescriptionId(step.index)"
                            triggers="hover click blur"
                            placement="bottom"
                        >
                            <template #title>{{ step.title }}</template>
                            {{ step.description }}
                        </b-popover>
                    </div>

                    <span class="manifest-pipeline-editor__step-badges">
                        <b-badge
                            v-if="step.issues.length"
                            variant="light-warning"
                            :title="elementIssueSummary(step)"
                        >
                            {{ elementIssueLabel(step) }}
                        </b-badge>
                        <b-badge variant="light-secondary">
                            {{ step.kind }}
                        </b-badge>
                    </span>

                    <div class="manifest-pipeline-editor__step-key">
                        <code>{{ step.key }}@{{ step.version }}</code>
                    </div>

                    <div class="manifest-pipeline-editor__step-type-flow text-muted">
                        {{ step.flowInputType }}
                        <feather-icon icon="ArrowRightIcon" size="13" class="mx-25" />
                        {{ step.flowOutputType }}
                    </div>

                    <div class="manifest-pipeline-editor__step-actions">
                        <b-button
                            v-if="step.argumentDescriptors.length || (allowConditions && availableConditions.length)"
                            size="sm"
                            :variant="isStepExpanded(step.index) ? 'flat-primary' : 'flat-secondary'"
                            class="btn-icon"
                            :title="isStepExpanded(step.index) ? 'Close arguments' : 'Configure arguments'"
                            :aria-expanded="isStepExpanded(step.index) ? 'true' : 'false'"
                            @click="toggleStep(step.index)"
                        >
                            <feather-icon icon="SlidersIcon" size="16" />
                        </b-button>
                        <b-button
                            size="sm"
                            variant="flat-secondary"
                            class="btn-icon"
                            title="Move step up"
                            :disabled="disabled || step.index === 0"
                            @click="moveStep(step.index, -1)"
                        >
                            <feather-icon icon="ArrowUpIcon" size="16" />
                        </b-button>
                        <b-button
                            size="sm"
                            variant="flat-secondary"
                            class="btn-icon"
                            title="Move step down"
                            :disabled="disabled || step.index === steps.length - 1"
                            @click="moveStep(step.index, 1)"
                        >
                            <feather-icon icon="ArrowDownIcon" size="16" />
                        </b-button>
                        <b-button
                            size="sm"
                            variant="flat-danger"
                            class="btn-icon"
                            title="Remove step"
                            :disabled="disabled"
                            @click="removeStep(step.index)"
                        >
                            <feather-icon icon="Trash2Icon" size="16" />
                        </b-button>
                    </div>
                </div>

                <div
                    v-if="isStepExpanded(step.index) && (step.argumentDescriptors.length || allowConditions)"
                    class="manifest-pipeline-editor__arguments"
                >
                    <manifest-component-arguments
                        v-if="step.argumentDescriptors.length"
                        :value="step.arguments"
                        :schema="step.argumentSchema"
                        :disabled="disabled"
                        @input="updateStepArguments(step.index, $event)"
                    />

                    <div
                        v-if="allowConditions && availableConditions.length"
                        class="manifest-pipeline-editor__step-condition"
                        :class="{'mt-1': step.argumentDescriptors.length}"
                    >
                        <b-form-group
                            label="Run only when"
                            :label-for="stepConditionId(step.index)"
                            class="mb-1"
                        >
                            <v-select
                                :id="stepConditionId(step.index)"
                                :value="step.conditionComponent"
                                :options="availableConditions"
                                :get-option-key="componentOptionKey"
                                :disabled="disabled"
                                label="title"
                                placeholder="Always run this step"
                                @input="updateStepCondition(step.index, $event)"
                            />
                        </b-form-group>

                        <manifest-component-arguments
                            v-if="step.conditionComponent && step.conditionArguments.length"
                            :value="step.when.arguments"
                            :schema="step.conditionComponent.argumentSchema"
                            :disabled="disabled"
                            @input="updateStepConditionArguments(step.index, $event)"
                        />
                    </div>
                </div>
                </template>
            </article>

            <div v-if="showTargetType && targetField" class="manifest-pipeline-editor__target">
                <b-badge
                    v-if="typeFlow.targetIssue"
                    variant="light-warning"
                    :title="typeFlow.targetIssue"
                >
                    Type mismatch
                </b-badge>
                <span class="text-muted">Canonical target type</span>
                <b-badge variant="light-primary">{{ targetField.valueType }}</b-badge>
            </div>
        </div>

        <div v-else class="manifest-pipeline-editor__empty">
            <feather-icon icon="GitMergeIcon" size="20" class="mr-75" />
            <span>{{ emptyText }}</span>
        </div>
    </div>
</template>

<script>
import {
    BBadge,
    BButton,
    BButtonGroup,
    BFormGroup,
    BPopover,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ManifestComponentArguments from './ManifestComponentArguments.vue'
import {
    argumentDescriptors,
    argumentIssues,
    initialArguments,
} from './manifestArgumentSchema'
import {
    analyzePipelineTypeFlow,
    isConditionalPipelineElement,
} from './manifestPipelineTypeFlow'

const PIPELINE_KINDS = ['normalizer', 'validator', 'transformer']
const BRANCH_KINDS = ['value_extractor', 'normalizer', 'validator', 'transformer']

export default {
    name: 'ManifestPipelineEditor',
    components: {
        BBadge,
        BButton,
        BButtonGroup,
        BFormGroup,
        BPopover,
        ManifestComponentArguments,
        vSelect,
    },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        components: {
            type: Array,
            default: () => [],
        },
        targetField: {
            type: Object,
            default: null,
        },
        allowedKinds: {
            type: Array,
            default: () => PIPELINE_KINDS,
        },
        allowConditions: {
            type: Boolean,
            default: true,
        },
        initialType: {
            type: String,
            default: 'mixed',
        },
        branchMode: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: 'Pipeline',
        },
        description: {
            type: String,
            default: 'Ordered normalization, validation, and transformation steps',
        },
        pickerLabel: {
            type: String,
            default: 'Pipeline component',
        },
        pickerPlaceholder: {
            type: String,
            default: 'Choose a normalizer, validator, or transformer',
        },
        emptyText: {
            type: String,
            default: 'Add normalization and validation steps when the source value needs processing.',
        },
        requiresTargetField: {
            type: Boolean,
            default: true,
        },
        showTargetType: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedComponent: null,
            selectedCondition: null,
            showPicker: false,
            expandedStepIndex: null,
            pickerMode: 'step',
        }
    },
    computed: {
        availableComponents() {
            return this.components.filter(component => (
                this.allowedKinds.includes(component.kind)
                && component.status !== 'deprecated'
            ))
        },
        availableConditions() {
            return this.components.filter(component => (
                component.kind === 'condition'
                && component.status !== 'deprecated'
            ))
        },
        branchKinds() {
            return BRANCH_KINDS
        },
        normalizedValue() {
            return this.value.map(element => this.normalizeElement(element))
        },
        typeFlow() {
            return analyzePipelineTypeFlow({
                elements: this.normalizedValue,
                components: this.components,
                initialType: this.initialType,
                branchMode: this.branchMode,
                targetType: this.showTargetType && this.targetField
                    ? this.targetField.valueType
                    : null,
            })
        },
        steps() {
            return this.normalizedValue.map((normalized, index) => {
                const flow = this.typeFlow.elements[index]
                if (isConditionalPipelineElement(normalized)) {
                    const conditionComponent = this.findComponent(
                        normalized.when.key,
                        normalized.when.version
                    )
                    const conditionSchema = conditionComponent && conditionComponent.argumentSchema
                        ? conditionComponent.argumentSchema
                        : {}
                    const conditionArgumentIssues = argumentIssues(
                        conditionSchema,
                        normalized.when.arguments
                    )
                    const branchArgumentIssues = normalized.then.reduce((issues, reference) => {
                        const branchComponent = this.findComponent(reference.key, reference.version)
                        if (!branchComponent) {
                            return issues
                        }

                        return [
                            ...issues,
                            ...argumentIssues(
                                branchComponent.argumentSchema || {},
                                reference.arguments
                            ),
                        ]
                    }, [])

                    return {
                        ...normalized,
                        index,
                        elementType: 'conditional',
                        renderKey: `conditional:${normalized.when.key}@${normalized.when.version}:${index}`,
                        title: conditionComponent ? conditionComponent.title : normalized.when.key,
                        description: conditionComponent ? conditionComponent.description : null,
                        conditionComponent,
                        conditionArguments: argumentDescriptors(conditionSchema),
                        argumentIssues: conditionArgumentIssues,
                        flowInputType: flow ? flow.inputType : this.initialType,
                        flowOutputType: flow ? flow.outputType : this.initialType,
                        branchOutputType: normalized.then.length && flow && flow.then.length
                            ? flow.then[flow.then.length - 1].outputType
                            : null,
                        flowIssues: flow ? flow.issues : [],
                        issues: [
                            ...conditionArgumentIssues,
                            ...branchArgumentIssues,
                            ...(flow ? flow.issues : []),
                        ],
                    }
                }

                const component = this.findComponent(normalized.key, normalized.version)
                const argumentSchema = component && component.argumentSchema
                    ? component.argumentSchema
                    : {}
                const descriptors = argumentDescriptors(argumentSchema)
                const componentArgumentIssues = argumentIssues(
                    argumentSchema,
                    normalized.arguments
                )
                const conditionComponent = normalized.when
                    ? this.findComponent(normalized.when.key, normalized.when.version)
                    : null
                const conditionSchema = conditionComponent && conditionComponent.argumentSchema
                    ? conditionComponent.argumentSchema
                    : {}
                const conditionArgumentIssues = normalized.when
                    ? argumentIssues(conditionSchema, normalized.when.arguments)
                    : []

                return {
                    ...normalized,
                    index,
                    elementType: 'step',
                    renderKey: `${normalized.key}@${normalized.version}:${index}`,
                    title: component ? component.title : normalized.key,
                    description: component ? component.description : null,
                    kind: component ? component.kind : 'unknown',
                    inputType: component && component.inputType ? component.inputType : 'mixed',
                    outputType: component && component.outputType ? component.outputType : 'mixed',
                    argumentSchema,
                    argumentDescriptors: descriptors,
                    argumentIssues: componentArgumentIssues,
                    conditionComponent,
                    conditionArguments: argumentDescriptors(conditionSchema),
                    conditionArgumentIssues,
                    flowInputType: flow ? flow.inputType : this.initialType,
                    flowOutputType: flow ? flow.outputType : this.initialType,
                    flowIssues: flow ? flow.issues : [],
                    issues: [
                        ...componentArgumentIssues,
                        ...conditionArgumentIssues,
                        ...(flow ? flow.issues : []),
                    ],
                }
            })
        },
    },
    watch: {
        targetField() {
            this.showPicker = false
            this.selectedComponent = null
            this.selectedCondition = null
            this.expandedStepIndex = null
            this.pickerMode = 'step'
        },
    },
    methods: {
        componentOptionKey(component) {
            return `${component.key}@${component.version}`
        },
        stepDescriptionId(index) {
            return `manifest-pipeline-step-description-${this._uid}-${index}`
        },
        stepConditionId(index) {
            return `manifest-pipeline-step-condition-${this._uid}-${index}`
        },
        branchConditionId(index) {
            return `manifest-pipeline-branch-condition-${this._uid}-${index}`
        },
        findComponent(key, version) {
            return this.components.find(component => (
                component.key === key
                && (version === null || component.version === version)
            )) || null
        },
        normalizeReference(reference) {
            if (typeof reference === 'string') {
                const component = this.findComponent(reference, null)
                return {
                    key: reference,
                    version: component ? component.version : 1,
                    arguments: {},
                }
            }

            return {
                key: reference.key,
                version: reference.version,
                arguments: reference.arguments && typeof reference.arguments === 'object'
                    ? reference.arguments
                    : {},
            }
        },
        normalizeElement(element) {
            if (isConditionalPipelineElement(element)) {
                return {
                    when: this.normalizeReference(element.when),
                    then: element.then.map(step => this.normalizeReference(step)),
                }
            }

            const normalized = this.normalizeReference(element)
            return {
                ...normalized,
                ...(element && element.when
                    ? { when: this.normalizeReference(element.when) }
                    : {}),
            }
        },
        normalizeStep(step) {
            return this.normalizeElement(step)
        },
        elementIssueLabel(step) {
            const messages = step.issues.map(issue => (
                typeof issue === 'string' ? issue : issue.message
            ))
            if (messages.some(message => (
                message === 'This argument is required.'
                || message === 'Condition is required.'
                || message === 'At least one then step is required.'
            ))) {
                return 'Configuration required'
            }
            if (step.flowIssues.length) {
                return 'Type mismatch'
            }
            return 'Configuration invalid'
        },
        elementIssueSummary(step) {
            return step.issues.map(issue => (
                typeof issue === 'string' ? issue : issue.message
            )).filter(Boolean).join(' ')
        },
        isStepExpanded(index) {
            return this.expandedStepIndex === index
        },
        toggleStep(index) {
            this.expandedStepIndex = this.isStepExpanded(index) ? null : index
        },
        addSelectedComponent(component) {
            if (!component || this.disabled) {
                return
            }

            this.$emit('input', [
                ...this.normalizedValue,
                {
                    key: component.key,
                    version: component.version,
                    arguments: initialArguments(component.argumentSchema),
                },
            ])
            this.expandedStepIndex = argumentDescriptors(component.argumentSchema).length
                ? this.value.length
                : null
            this.selectedComponent = null
            this.showPicker = false
            this.pickerMode = 'step'
        },
        addConditionalBranch(condition) {
            if (!condition || this.disabled || !this.allowConditions) {
                return
            }

            this.$emit('input', [
                ...this.normalizedValue,
                {
                    when: {
                        key: condition.key,
                        version: condition.version,
                        arguments: initialArguments(condition.argumentSchema),
                    },
                    then: [],
                },
            ])
            this.expandedStepIndex = this.value.length
            this.selectedCondition = null
            this.showPicker = false
            this.pickerMode = 'step'
        },
        updateStepArguments(index, argumentsValue) {
            if (this.disabled) {
                return
            }

            const pipeline = [...this.normalizedValue]
            pipeline.splice(index, 1, {
                ...pipeline[index],
                arguments: argumentsValue,
            })
            this.$emit('input', pipeline)
        },
        updateStepCondition(index, condition) {
            if (this.disabled) {
                return
            }

            const pipeline = [...this.normalizedValue]
            const step = { ...pipeline[index] }
            if (condition) {
                step.when = {
                    key: condition.key,
                    version: condition.version,
                    arguments: initialArguments(condition.argumentSchema),
                }
            } else {
                delete step.when
            }
            pipeline.splice(index, 1, step)
            this.$emit('input', pipeline)
        },
        updateStepConditionArguments(index, argumentsValue) {
            if (this.disabled) {
                return
            }

            const pipeline = [...this.normalizedValue]
            pipeline.splice(index, 1, {
                ...pipeline[index],
                when: {
                    ...pipeline[index].when,
                    arguments: argumentsValue,
                },
            })
            this.$emit('input', pipeline)
        },
        updateBranchCondition(index, condition) {
            if (!condition || this.disabled) {
                return
            }

            const pipeline = [...this.normalizedValue]
            pipeline.splice(index, 1, {
                ...pipeline[index],
                when: {
                    key: condition.key,
                    version: condition.version,
                    arguments: initialArguments(condition.argumentSchema),
                },
            })
            this.$emit('input', pipeline)
        },
        updateBranchConditionArguments(index, argumentsValue) {
            if (this.disabled) {
                return
            }

            const pipeline = [...this.normalizedValue]
            pipeline.splice(index, 1, {
                ...pipeline[index],
                when: {
                    ...pipeline[index].when,
                    arguments: argumentsValue,
                },
            })
            this.$emit('input', pipeline)
        },
        updateBranchThen(index, thenSteps) {
            if (this.disabled) {
                return
            }

            const pipeline = [...this.normalizedValue]
            pipeline.splice(index, 1, {
                ...pipeline[index],
                then: thenSteps.map(step => this.normalizeReference(step)),
            })
            this.$emit('input', pipeline)
        },
        moveStep(index, offset) {
            if (this.disabled) {
                return
            }

            const targetIndex = index + offset
            if (targetIndex < 0 || targetIndex >= this.value.length) {
                return
            }

            const pipeline = [...this.normalizedValue]
            const [step] = pipeline.splice(index, 1)
            pipeline.splice(targetIndex, 0, step)
            if (this.expandedStepIndex === index) {
                this.expandedStepIndex = targetIndex
            } else if (this.expandedStepIndex === targetIndex) {
                this.expandedStepIndex = index
            }
            this.$emit('input', pipeline)
        },
        removeStep(index) {
            if (this.disabled) {
                return
            }

            if (this.expandedStepIndex === index) {
                this.expandedStepIndex = null
            } else if (this.expandedStepIndex > index) {
                this.expandedStepIndex -= 1
            }
            this.$emit('input', this.value
                .filter((step, stepIndex) => stepIndex !== index)
                .map(step => this.normalizeElement(step)))
        },
    },
}
</script>

<style lang="scss" scoped>
.manifest-pipeline-editor__picker,
.manifest-pipeline-editor__empty {
    padding: 1rem;
    border-radius: .357rem;
    background: rgba(0, 0, 0, .035);
}

.manifest-pipeline-editor__add-button {
    flex-shrink: 0;
    white-space: nowrap;
}

.manifest-pipeline-editor__step {
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #fff;
}

.manifest-pipeline-editor__step--conditional {
    border-left: 3px solid var(--primary);
}

.manifest-pipeline-editor__step--conditional .manifest-pipeline-editor__step-title {
    grid-column: 2;
}

.manifest-pipeline-editor__step--conditional .manifest-pipeline-editor__step-badges {
    grid-column: 2 / 4;
    grid-row: 3;
    justify-self: start;
}

.manifest-pipeline-editor__step--conditional .manifest-pipeline-editor__step-key {
    grid-row: 2;
}

.manifest-pipeline-editor__step--conditional .manifest-pipeline-editor__step-actions {
    grid-row: 1;
}

.manifest-pipeline-editor__conditional-label {
    color: var(--primary);
    font-size: .75rem;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
}

.manifest-pipeline-editor__step-main {
    display: grid;
    grid-template-columns: 1.75rem minmax(0, 1fr) auto;
    grid-template-rows: auto auto auto;
    column-gap: .75rem;
    row-gap: .25rem;
    align-items: center;
    padding: .75rem;
}

.manifest-pipeline-editor__option-description {
    display: block;
    max-width: 32rem;
    margin-top: .35rem;
    white-space: normal;
}

.manifest-pipeline-editor__info {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    color: var(--primary);
    cursor: help;
    outline: none;
}

.manifest-pipeline-editor__info:hover,
.manifest-pipeline-editor__info:focus-visible {
    background: rgba(115, 103, 240, .12);
}

.manifest-pipeline-editor__step + .manifest-pipeline-editor__step {
    margin-top: .5rem;
}

.manifest-pipeline-editor__step-number {
    display: flex;
    grid-column: 1;
    grid-row: 1;
    width: 1.75rem;
    height: 1.75rem;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: var(--primary);
    background: rgba(115, 103, 240, .12);
    font-weight: 600;
}

.manifest-pipeline-editor__step-title {
    display: flex;
    grid-column: 2;
    grid-row: 1;
    min-width: 0;
    align-items: center;
}

.manifest-pipeline-editor__step-badges {
    display: inline-flex;
    grid-column: 3;
    grid-row: 1;
    align-items: center;
    justify-self: end;
    gap: .5rem;
}

.manifest-pipeline-editor__step-key {
    grid-column: 2 / 4;
    grid-row: 2;
    min-width: 0;
}

.manifest-pipeline-editor__step-key code {
    overflow-wrap: anywhere;
}

.manifest-pipeline-editor__step-type-flow {
    display: flex;
    grid-column: 2;
    grid-row: 3;
    align-items: center;
}

.manifest-pipeline-editor__conditional-flow {
    display: grid;
    grid-column: 2 / 4;
    grid-row: 4;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: .35rem .75rem;
    margin-top: .25rem;
    padding-top: .5rem;
    border-top: 1px solid #ebe9f1;
}

.manifest-pipeline-editor__flow-path {
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: flex-start;
    gap: .1rem;
}

.manifest-pipeline-editor__flow-path small {
    color: var(--manifest-secondary-text-color, #45434c);
    white-space: nowrap;
}

.manifest-pipeline-editor__flow-path code {
    overflow: hidden;
    text-overflow: ellipsis;
}

.manifest-pipeline-editor__flow-path--result {
    font-weight: 600;
}

.manifest-pipeline-editor__step-actions {
    display: flex;
    grid-column: 3;
    grid-row: 3;
    justify-self: end;
}

.manifest-pipeline-editor__arguments {
    padding: 1rem;
    border-top: 1px solid #ebe9f1;
    background: rgba(0, 0, 0, .015);
}

.manifest-pipeline-editor__step-condition {
    padding-top: 1rem;
    border-top: 1px solid #ebe9f1;
}

.manifest-pipeline-editor__branch {
    background: rgba(115, 103, 240, .035);
}

.manifest-pipeline-editor__then {
    margin-top: 1rem;
    padding: 1rem;
    border-left: 2px solid rgba(115, 103, 240, .35);
    border-radius: 0 .357rem .357rem 0;
    background: #fff;
}

.manifest-pipeline-editor__target {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: .5rem;
    margin-top: .75rem;
}

.manifest-pipeline-editor__empty {
    display: flex;
    align-items: center;
    color: var(--manifest-secondary-text-color, #45434c);
}
</style>
